import { UpgradeTracking } from "@/components/molecules/Modals/ModalController";
import { UpdateFilterType } from "@/pages/Players/FiltersPanel/filterTypes";
import { AppDispatch } from "@/redux/store";
import { type ClubImportOrigin, promptClubImport } from "@repo/shared/redux";
import type { ClubImportFeatureId } from "@repo/shared/redux";
import { useClubImportFeature } from "@repo/shared/redux";
import { useDispatch } from "react-redux";

export type MyClubFilterType = "own-evo-player" | "own-player" | "active-squad";

export type MyClubQuickFilterKey = "my evolutions" | "squad" | "my club";

/**
 * Maps a {@link MyClubFilterType} to the label used by the `trackQuickFilter`
 * Mixpanel events. Exported so callers can build their (subsection-specific)
 * tracking payloads without duplicating the mapping.
 */
export const quickFilterKeyFromType = (
  type: MyClubFilterType,
): MyClubQuickFilterKey => {
  switch (type) {
    case "own-evo-player":
      return "my evolutions";
    case "active-squad":
      return "squad";
    default:
      return "my club";
  }
};

/**
 * Tracking hooks. `onFilterApplied` (optional) fires whenever the filter is
 * actually applied (i.e. `updateFilter` runs).
 *
 * `upgradeTracking` is no longer read: these filters are free, so there is no
 * upgrade to track. Kept in the type so the six call sites don't have to change
 * in the same commit; drop it once they're cleaned up.
 */
export type MyClubFilterTracking = {
  onFilterApplied?: (type: MyClubFilterType) => void;
  upgradeTracking?: UpgradeTracking;
  /**
   * Where this filter lives. Required because the gate these filters raise ends
   * in a signup several screens away, and the origin is unrecoverable by then —
   * see clubImportPrompt.noteClubImportIntent. The subsection defaults to the
   * gated feature ("see-own-players"), which is usually more precise than
   * anything the call site would pass.
   */
  origin: ClubImportOrigin;
};

/**
 * Centralizes the "My Club / My Evolutions / Squad" gating logic.
 *
 * These filters read the user's imported club. They cost nothing — the gate is
 * whether there is a club to read, not what the user pays. Any logged-in user
 * who has imported gets in, on any tier.
 */
/*
  One table, so the access check and the gate it raises cannot disagree about
  which feature a filter is. My Evolutions used to be login-only rather than
  club-import gated, because evolved players could be typed in by hand without
  ever importing. Hand-typed evolutions are gone, so an evolution collection
  can only come from an import — and the login wall had started answering the
  wrong question: a logged-in user without a club got in and found nothing.
*/
const FEATURE_BY_FILTER: Record<MyClubFilterType, ClubImportFeatureId> = {
  "own-player": "see-own-players",
  "own-evo-player": "see-own-evo-players",
  "active-squad": "see-active-squad",
};

export const useMyClubFilter = (
  updateFilter: (f: UpdateFilterType) => void,
  tracking: MyClubFilterTracking,
) => {
  const ownPlayers = useClubImportFeature("see-own-players");
  const ownEvoPlayers = useClubImportFeature("see-own-evo-players");
  const activeSquad = useClubImportFeature("see-active-squad");
  const dispatch = useDispatch<AppDispatch>();

  return (f: UpdateFilterType, type: MyClubFilterType) => {
    const apply = () => {
      updateFilter(f);
      tracking.onFilterApplied?.(type);
    };

    const access = {
      "own-player": ownPlayers,
      "own-evo-player": ownEvoPlayers,
      "active-squad": activeSquad,
    }[type];

    if (access.allowed) {
      apply();
      return;
    }

    // Profile still in flight — a no-op beats a wrong answer.
    if (access.reason === "loading") return;

    // Logged out and "no club yet" share a gate: the tutorial handles sign-in.
    dispatch(promptClubImport(FEATURE_BY_FILTER[type], tracking.origin));
  };
};
